<script>
  import { onMount, createEventDispatcher } from "svelte";
  import { cart } from "../models/cart.js";
  import { formatPrice } from "../services/utilities.js";
  import Alert from "./Alert.svelte";

  const dispatch = createEventDispatcher();

  export var isAdmin = false;
  export var product;
  export var compact;
  export var editable;
  export var returnToStock = null;
  export var orderID = null;
  let returnQuantity;
  let dimensions = null;
  let pieceCount = null;

  let showMissingPiecesModal = false;

  function closeMissingPiecesModal() {
    showMissingPiecesModal = false;
    // Notify parent that conditions may have changed inside the modal so the
    // surrounding admin order view can refresh (e.g. ScoreBadge, condition counts).
    dispatch("conditionschanged", { orderID });
  }

  // Show the Report Missing Pieces button only in admin contexts on rental items,
  // and only when we know which order the item belongs to.
  $: canReportMissingPieces =
    isAdmin &&
    orderID &&
    product &&
    product.product_type &&
    product.product_type.name === "Puzzle (Rental)";

  function imgUrl(image) {
    return image.url + image.filename + "-sm." + image.extension;
  }

  onMount(() => {
    if (product.attributes) {
      product.attributes.forEach((a) => {
        if (a.name === "Dimensions") {
          dimensions = a.value;
        } else if (a.name === "Piece Count") {
          pieceCount = a.value;
        }
      });
    }
  });
</script>

<li class="cart-item" class:compact class:static={!editable} class:is-admin={isAdmin}>
  {#if product.changed}
    <div class="cart-item-alert">
      <Alert type="danger" icon="warning">
        <ul class="list-unstyled">
          {#each product.changeMsgs as m}
            <li>{m}</li>
          {/each}
        </ul>
      </Alert>
    </div>
  {/if}

  <div class="cart-item-content">
    <div class="cart-item-summary">
      {#if !compact}
        <div class="cart-item-image">
          {#if product.images && product.images.length && product.images[0].url != ""}
            <img
              src={imgUrl(product.images[0])}
              class="image-fill"
              alt={product.images[0].title}
            />
          {:else}
            <img
              src="/assets/logo.jpg"
              class="image-fill"
              alt="Missing Piece Logo"
            />
          {/if}
        </div>
      {/if}

      <div class="cart-item-info">
        {#if !isAdmin}
          {#if product.product_type.name == "Ticket" && product.event_id}
            <a href="/event/{product.event_id}" class="cart-item-title">
              {product.title}
            </a>
          {:else if product.product_type.name == "Ticket"}
            {product.title}
          {:else}
            <a
              href="/product/{product.title}/sku/{product.sku}"
              class="cart-item-title"
            >
              {product.title}
            </a>
          {/if}
        {:else}
          <a
            href="/admin/inventory/product/{product.sku}"
            class="cart-item-title"
            target="_blank"
            rel="noopener noreferrer"
          >
            {product.title}
          </a>
        {/if}
        {#if !compact}
          {#if dimensions}
            <span class="cart-item-sku">{dimensions}</span>
          {/if}
          {#if pieceCount}
            <span class="cart-item-sku">{pieceCount} Pieces</span>
          {/if}
          <span class="cart-item-sku">{product.sku}</span>
          <span class="cart-item-type">{product.product_type.name}</span>
        {/if}
        {#if product.product_type.name === "Subscription"}
          <div class="subscription-alert">
            <Alert type="warning" icon="warning">
              After you finish purchasing your chosen subscription plan, please
              wait for an email on more information on how to checkout your
              rentals.
            </Alert>
          </div>
        {/if}
      </div>
    </div>

    <div class="cart-item-quantity-price">
      <div class="cart-item-quantity">
        {#if !compact}
          <!--Only show add/minus btns on full cart page-->
          <span class="cart-item-quantity-value"
            >Quantity: {product.quantity}</span
          >
          <button
            class="btn cart-btn"
            on:click|stopPropagation={cart.remove(product)}
          >
            <svg class="icon">
              <use href="/icons.svg#minus" />
            </svg>
          </button>

          <!-- Only allow 1 rental ticket per order -->
          <!-- TODO: replace looking at the title with the product attribute include_rentals-->
          <button
            class="btn cart-btn"
            disabled={[1, 4, 5].indexOf(product.product_type.id) > -1 ||
              (product.product_type.id === 7 &&
                product.title.indexOf("Rentals") > -1)}
            on:click|stopPropagation={cart.add(product)}
          >
            <svg class="icon">
              <use href="/icons.svg#plus" />
            </svg>
          </button>
        {:else}
          <span class="cart-item-quantity-value">Qty:{product.quantity}</span>
        {/if}
      </div>
      <div class="cart-item-price">
        {#if product.discount_price}
          <s>{formatPrice(product.price * product.quantity)}</s>
          {formatPrice(product.discount_price * product.quantity)}
        {:else if product.sale_price}
          <s>{formatPrice(product.price * product.quantity)}</s>
          {formatPrice(product.sale_price * product.quantity)}
        {:else}{formatPrice(product.price * product.quantity)}{/if}
      </div>
    </div>

    <!--TODO: rip out for admin page only -->
    {#if returnToStock !== null}
      <div class="cart-return">
        {#if product.quantity > product.returned_quantity}
          <select bind:value={returnQuantity}>
            {#each [...Array(product.quantity - product.returned_quantity).keys()] as i}
              <option value={i + 1}>{i + 1}</option>
            {/each}
          </select>
          <button
            disabled={product.quantity === product.returned_quantity}
            class="btn"
            on:click={() => {
              returnToStock(product, returnQuantity);
              product.returned_quantity += returnQuantity;
            }}
          >
            Return to Stock
          </button>
        {:else}
          <button disabled class="btn">Returned</button>
        {/if}
      </div>
    {/if}

    {#if canReportMissingPieces}
      <div class="cart-missing-pieces">
        <button
          class="btn btn-danger"
          on:click={() => (showMissingPiecesModal = true)}
          title="Report missing pieces involving this rental"
        >
          Report Missing Pieces
        </button>
      </div>
    {/if}
  </div>

  {#if showMissingPiecesModal}
    <div
      class="mp-modal-overlay"
      on:click={closeMissingPiecesModal}
    >
      <div class="mp-modal" on:click|stopPropagation>
        <button
          class="mp-modal-close"
          on:click={closeMissingPiecesModal}
          title="Close"
        >×</button>
        {#await import("../templates/admin/MissingPieces.svelte")}
          <p class="mp-loading">Loading…</p>
        {:then mod}
          <svelte:component
            this={mod.default}
            preselectedSku={product.sku}
            launchingItem={{ orderID, orderItemID: product.order_product_id }}
          />
        {/await}
      </div>
    </div>
  {/if}
</li>

<style type="text/less">
  .cart-item {
    width: 100%;
    padding: var(--spacing-sm) 0;
    box-sizing: border-box;
    border-top: solid 1px var(--col-gray-80);

    .cart-item-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  // Admin-only layout: grow the summary so the right-side action buttons
  // (Return to Stock, Report Missing Pieces) line up across rows instead of
  // being distributed by space-between. Customer cart keeps the original layout.
  .cart-item.is-admin {
    .cart-item-content {
      justify-content: flex-start;
      gap: var(--spacing-sm);
    }

    .cart-item-summary {
      flex: 1 1 auto;
      min-width: 0;
    }

    .cart-item-quantity-price {
      align-self: center;
    }
  }

  .cart-item-summary {
    display: flex;
  }

  .cart-item-alert {
    flex: 0 0 100%;
    box-sizing: border-box;
    padding: 0 var(--spacing-sm) var(--spacing-sm);
  }

  .cart-item-image {
    width: calc(var(--line-height) * 3);
    height: calc(var(--line-height) * 3);
    padding: 0 var(--spacing-sm);
  }

  .cart-item-info {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    font-size: 0.9rem;
    line-height: var(--line-height);
  }
  .cart-item-title {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 1rem;
  }

  .cart-item-quantity {
    flex-shrink: 0;
    line-height: var(--line-height);
    text-align: left;
  }

  .cart-item-quantity-price {
    display: flex;
    align-items: center;
    align-self: flex-end;
  }

  .cart-item-quantity * {
    padding: var(--spacing-xs);
  }

  .cart-btn {
    border: none;
    padding: var(--spacing-xs);
  }
  .cart-btn .icon {
    display: block;
  }

  .cart-item-price {
    display: block;
    text-align: right;
    padding: 0 var(--spacing-sm);
    min-width: 6ch;
  }

  /* Compact Styles */
  .compact .cart-item-image,
  .compact .cart-item-price,
  .compact .cart-item-sku,
  .compact .cart-item-type,
  .compact .subscription-alert,
  .static .cart-item-quantity .btn {
    display: none;
  }

  .compact .cart-item-content {
    flex-direction: row;
  }

  .subscription-alert {
    width: 90%;
  }

  .cart-missing-pieces {
    margin-left: var(--spacing-sm);
    align-self: center;
  }

  .cart-return {
    display: flex;
    align-items: stretch;
    gap: 4px;
  }

  .cart-return select {
    box-sizing: border-box;
    align-self: stretch;
  }

  .mp-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    z-index: 1000;
    padding: var(--spacing);
    overflow-y: auto;
  }

  .mp-modal {
    background: white;
    border-radius: 6px;
    width: 100%;
    max-width: 1500px;
    margin: 2rem auto;
    position: relative;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  }

  .mp-modal-close {
    position: absolute;
    top: 8px;
    right: 12px;
    background: transparent;
    border: none;
    font-size: 1.75rem;
    line-height: 1;
    color: var(--col-gray-30);
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 4px;
    z-index: 1;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
      color: var(--col-gray-20);
    }
  }

  .mp-loading {
    padding: var(--spacing);
    text-align: center;
    color: var(--col-gray-30);
  }

  @media (max-width: 750px) {
    .cart-item {
      width: 100%;
      padding: var(--spacing-sm) 0;
      box-sizing: border-box;
      border-top: solid 1px var(--col-gray-80);

      .cart-item-content {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        flex-direction: column;
      }
    }

    .cart-item-summary {
      margin-bottom: var(--spacing-lg);
    }
  }
</style>
